import type { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { isPlayerPending } from "@/components/organisms/SBCs/RewardPlayerCard";
import Card from "@/design-system/Card/Card";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import type { ObjectiveBundle } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import {
  formatTime,
  objectiveGroupPath,
  objectiveReward,
} from "@repo/shared/utils";
import { X } from "lucide-react";
import { NavLink } from "react-router";

/**
 * A bundle group in small: its name, an optional note and tag under it, its
 * main reward and expiry. Links to the group page once the name is known;
 * `onRemove` adds the X that takes it out of the bundle.
 */
export const BundleGroupCard = ({
  group,
  note,
  tag,
  dim = false,
  onRemove,
}: {
  group: ObjectiveBundle["groups"][number];
  note?: string;
  tag?: React.ReactNode;
  /** Secondary groups: the ones the bundle only needs. */
  dim?: boolean;
  onRemove?: () => void;
}) => {
  const { t } = useTranslation("objectives");
  // Web's reward type: a player reward carries its player.
  const reward = objectiveReward(
    group.rewards[0] as SBCRewardType | undefined,
    t,
  );
  const player =
    reward?.type === "player" && reward.player ? reward.player : undefined;

  const card = (
    <Card
      hoverable={false}
      className={cn(
        "relative flex h-full items-center gap-x-3 p-3",
        dim && "border-neutral-800 opacity-80",
        // Room for the X, so it never sits on the reward.
        onRemove && "pr-7",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-y-1">
        <span className="line-clamp-2 text-sm font-semibold leading-tight text-foreground">
          {group.name ?? t("bar.groupFallback", { id: group.id })}
        </span>
        {note && (
          <span className="truncate text-[11px] text-foreground-faint">
            {note}
          </span>
        )}
        {tag}
        {reward?.name && (
          <span className="truncate text-xs text-foreground-muted">
            {reward.name}
          </span>
        )}
        <span className="truncate text-[11px] text-foreground-faint">
          {group.endTime
            ? t("card.expiresIn", {
                time: formatTime(group.endTime * 1000 - Date.now()),
              })
            : t("card.noExpiry")}
        </span>
      </div>
      {player && !isPlayerPending(player) ? (
        <div className="shrink-0">
          <PlayerCard
            player={player}
            position={player.preferredPosition}
            scale={0.3}
            hidePrice
          />
        </div>
      ) : (
        reward && (
          <img
            loading="lazy"
            className="w-10 shrink-0"
            alt={reward.name}
            src={extractRewardUrl(reward)}
          />
        )
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={t("plan.removeGroup")}
          onClick={(e) => {
            // The card is a link.
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full text-white hover:bg-white/15"
        >
          <X className="size-3.5" />
        </button>
      )}
    </Card>
  );

  return group.name ? (
    <NavLink
      to={objectiveGroupPath({ id: group.id, name: group.name })}
      className="h-full"
    >
      {card}
    </NavLink>
  ) : (
    card
  );
};
